/** Settings — goals, units, sync, backup. */
import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Card, Field, fmt } from '../ui/bits';
import { notify, settings, updateSettings } from '../store';
import { exportAll, exportFilename, importAll, toBlob, wipeAll } from '../data/export';
import { checkStatus, pullHealth, pullOura } from '../data/sync';
import { applyLegacy, importLegacyText, mapOldState, readLegacyLocalStorage } from '../data/legacy';
import { markLegacyMigrated, refreshAll } from '../store';
import { OFF_ATTRIBUTION } from '../data/openfoodfacts';
import { getAll } from '../data/db';
import { relativeAge } from '../lib/time';

export function Settings(): JSX.Element {
  const s = settings.value;
  const [busy, setBusy] = useState('');

  const run = async (key: string, fn: () => Promise<string>) => {
    setBusy(key);
    try {
      notify(await fn());
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed', true);
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <Card title="Daily goals">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <GoalField label="Calories" value={s.goals.kcal} onChange={(v) => void updateSettings({ goals: { ...s.goals, kcal: v } })} />
          <GoalField label="Protein (g)" value={s.goals.protein} onChange={(v) => void updateSettings({ goals: { ...s.goals, protein: v } })} />
          <GoalField label="Carbs (g)" value={s.goals.carbs} onChange={(v) => void updateSettings({ goals: { ...s.goals, carbs: v } })} />
          <GoalField label="Fat (g)" value={s.goals.fat} onChange={(v) => void updateSettings({ goals: { ...s.goals, fat: v } })} />
        </div>
      </Card>

      <Card title="Units">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <Field label="Weight">
            <select class="select" value={s.weightUnit}
              onChange={(e) => void updateSettings({ weightUnit: (e.target as HTMLSelectElement).value as 'kg' | 'lb' })}>
              <option value="lb">Pounds</option>
              <option value="kg">Kilograms</option>
            </select>
          </Field>
          <Field label="Distance">
            <select class="select" value={s.distanceUnit}
              onChange={(e) => void updateSettings({ distanceUnit: (e.target as HTMLSelectElement).value as 'km' | 'mi' })}>
              <option value="mi">Miles</option>
              <option value="km">Kilometres</option>
            </select>
          </Field>
        </div>
      </Card>

      <Card title="Sync">
        <div class="stack">
          <Field label="Endpoint" hint="The address this app is served from, unless you host the API elsewhere.">
            <input class="input" style={{ fontFamily: 'var(--sans)' }} placeholder="https://reta.benops.dev"
              value={s.sync.endpoint ?? ''}
              onInput={(e) => void updateSettings({ sync: { ...s.sync, endpoint: (e.target as HTMLInputElement).value } })} />
          </Field>
          <Field label="Token" hint="The same value you set as INGEST_TOKEN on the Worker.">
            <input class="input" type="password" autocomplete="off"
              value={s.sync.token ?? ''}
              onInput={(e) => void updateSettings({ sync: { ...s.sync, token: (e.target as HTMLInputElement).value } })} />
          </Field>

          <div class="row wrap" style={{ gap: '8px' }}>
            <button class="btn btn-sm" disabled={busy !== ''} onClick={() => void run('status', async () => {
              const st = await checkStatus();
              return `Connected. ${st.queued} waiting${st.ouraConfigured ? ', Oura ready' : ', no Oura token'}.`;
            })}>{busy === 'status' ? '…' : 'Test'}</button>

            <button class="btn btn-sm" disabled={busy !== ''} onClick={() => void run('health', async () => {
              const out = await pullHealth();
              if (out.unmapped.length) {
                return `${out.message} Unrecognised: ${out.unmapped.join(', ')}`;
              }
              return out.message;
            })}>{busy === 'health' ? '…' : 'Pull Apple Health'}</button>

            <button class="btn btn-sm" disabled={busy !== ''} onClick={() => void run('oura', async () => (await pullOura()).message)}>
              {busy === 'oura' ? '…' : 'Pull Oura'}
            </button>
          </div>

          <p class="note">
            {s.sync.lastHealthSyncAt ? `Health last synced ${relativeAge(s.sync.lastHealthSyncAt)} ago. ` : 'Health has never synced. '}
            {s.sync.lastOuraSyncAt ? `Oura ${relativeAge(s.sync.lastOuraSyncAt)} ago.` : 'Oura has never synced.'}
          </p>
        </div>
      </Card>

      <Card title="Parsing assist">
        <div class="stack">
          <p class="note">
            Off by default. When on, the meal text you type is sent to a model you choose to
            improve matching; macros still come from the built-in USDA data.
          </p>
          <label class="row" style={{ gap: '10px' }}>
            <input type="checkbox" checked={s.llm.enabled}
              onChange={(e) => void updateSettings({ llm: { ...s.llm, enabled: (e.target as HTMLInputElement).checked, provider: s.llm.provider ?? 'anthropic' } })} />
            <span class="label label-lit">Use a model to read meals</span>
          </label>
          {s.llm.enabled ? (
            <>
              <Field label="Provider">
                <select class="select" value={s.llm.provider ?? 'anthropic'}
                  onChange={(e) => void updateSettings({ llm: { ...s.llm, provider: (e.target as HTMLSelectElement).value as 'anthropic' | 'openai-compatible' } })}>
                  <option value="anthropic">Claude (API key)</option>
                  <option value="openai-compatible">Local server (OpenAI-compatible)</option>
                </select>
              </Field>
              {s.llm.provider === 'openai-compatible' ? (
                <Field label="Server" hint="e.g. http://mac-studio.local:1234/v1 — LM Studio, Ollama or llama.cpp.">
                  <input class="input" style={{ fontFamily: 'var(--sans)' }} placeholder="http://localhost:1234/v1"
                    value={s.llm.baseUrl ?? ''}
                    onInput={(e) => void updateSettings({ llm: { ...s.llm, baseUrl: (e.target as HTMLInputElement).value } })} />
                </Field>
              ) : null}
              <Field label="API key" hint={s.llm.provider === 'anthropic' ? 'A Claude API key. Stored only on this device.' : 'Optional.'}>
                <input class="input" type="password" autocomplete="off" value={s.llm.apiKey ?? ''}
                  onInput={(e) => void updateSettings({ llm: { ...s.llm, apiKey: (e.target as HTMLInputElement).value } })} />
              </Field>
              <Field label="Model" hint={s.llm.provider === 'anthropic' ? 'Blank uses claude-opus-5.' : 'The model name your server exposes.'}>
                <input class="input" placeholder={s.llm.provider === 'anthropic' ? 'claude-opus-5' : 'default'} value={s.llm.model ?? ''}
                  onInput={(e) => void updateSettings({ llm: { ...s.llm, model: (e.target as HTMLInputElement).value } })} />
              </Field>
            </>
          ) : null}
        </div>
      </Card>

      <Card title="Import history">
        <div class="stack">
          <p class="note">
            Bring across data from the previous Reta or its files. The format is recognised
            automatically: the old app's backup, <code>ben.json</code>, inbox <code>.jsonl</code>,
            <code>oura-ledger.jsonl</code>, <code>apple_health.jsonl</code>, <code>apple_workouts.jsonl</code>
            or <code>renpho.json</code>. Re-importing the same file changes nothing.
          </p>
          <button class="btn btn-full" disabled={busy !== ''} onClick={() => void run('legacy-local', async () => {
            const state = readLegacyLocalStorage();
            if (!state) return 'No previous Reta data is stored on this phone.';
            const report = await applyLegacy(mapOldState(state));
            await markLegacyMigrated();
            await refreshAll();
            const n = Object.values(report.written).reduce((a, b) => a + b, 0) + report.metricsDays;
            return `Imported ${n} records from the previous Reta on this phone.`;
          })}>{busy === 'legacy-local' ? '…' : "Import from this phone's previous Reta"}</button>
          <FilePick
            accept=".jsonl,.json,text/plain,application/json"
            label="Choose a file to import"
            onFile={async (file) => {
              const out = await importLegacyText(await file.text());
              if ('error' in out) { notify(out.error, true); return; }
              await refreshAll();
              const counts = Object.entries(out.inventory.counts).map(([k, v]) => `${v} ${k}`).join(', ');
              const skipped = out.inventory.skipped.length;
              notify(`Imported ${counts || 'nothing new'}${skipped ? ` · ${skipped} items not carried (see README)` : ''}.`);
            }}
          />
        </div>
      </Card>

      {s.profile ? (
        <Card title="Profile (from the previous Reta)">
          <p class="note">
            {s.profile.name ? `${s.profile.name}. ` : ''}
            {s.profile.startedOn ? `Started ${s.profile.startedOn}. ` : ''}
            {typeof s.profile.startWeightKg === 'number' ? `Start weight ${fmt(s.weightUnit === 'lb' ? s.profile.startWeightKg * 2.20462262185 : s.profile.startWeightKg, 1)} ${s.weightUnit}. ` : ''}
            {s.profile.allergies?.length ? `Allergies: ${s.profile.allergies.join(', ')}. ` : ''}
            {s.profile.eatingWindow ? `Eating window ${s.profile.eatingWindow.start}–${s.profile.eatingWindow.end}. ` : ''}
          </p>
          <p class="note" style={{ marginTop: '6px' }}>Carried over as you recorded it. Nothing in the app acts on it.</p>
        </Card>
      ) : null}

      <Card title="Backup">
        <div class="stack">
          <button class="btn btn-full" onClick={() => void run('export', async () => {
            const file = await exportAll();
            const url = URL.createObjectURL(toBlob(file));
            const a = document.createElement('a');
            a.href = url;
            a.download = exportFilename();
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            const n = Object.values(file.counts).reduce((x, y) => x + y, 0);
            return `Exported ${n} records.`;
          })}>Export everything</button>

          <FilePick
            accept="application/json,.json"
            label="Restore from a backup"
            onFile={async (file) => {
              const parsed = JSON.parse(await file.text());
              const result = await importAll(parsed, 'merge');
              if (!result.ok) { notify(result.errors[0] ?? 'Import failed', true); return; }
              const n = Object.values(result.imported).reduce((x, y) => x + y, 0);
              notify(`Restored ${n} records.`);
              location.reload();
            }}
          />
          <p class="note">
            Restoring merges: anything logged since the backup is kept.
          </p>
        </div>
      </Card>

      <Card title="Storage">
        <StorageInfo />
      </Card>

      <Card title="About">
        <p class="prose" style={{ margin: 0, fontSize: '12px' }}>
          Reta keeps everything on this device. Nothing is sent anywhere except the sync
          endpoint you configured above.
        </p>
        <div class="divider" />
        <p class="prose" style={{ margin: 0, fontSize: '11px' }}>
          Food data from the U.S. Department of Agriculture, FoodData Central (public domain).
          {' '}{OFF_ATTRIBUTION}
        </p>
        <div class="divider" />
        <p class="prose" style={{ margin: 0, fontSize: '11px' }}>
          This app is a log and a calculator. It does not give medical, dosing or safety advice.
        </p>
      </Card>

      <Card title="Danger">
        <button class="btn btn-full" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
          onClick={() => {
            if (!confirm('Delete every record on this device? Export a backup first — this cannot be undone.')) return;
            void wipeAll().then(() => location.reload());
          }}>Delete all data</button>
      </Card>
    </>
  );
}

function GoalField(props: { label: string; value?: number | undefined; onChange: (v: number | undefined) => void }): JSX.Element {
  return (
    <Field label={props.label}>
      <input class="input" inputMode="numeric" placeholder="—"
        value={props.value !== undefined ? String(props.value) : ''}
        onInput={(e) => {
          const raw = (e.target as HTMLInputElement).value.trim();
          if (!raw) { props.onChange(undefined); return; }
          const v = Number(raw);
          if (Number.isFinite(v) && v >= 0) props.onChange(v);
        }} />
    </Field>
  );
}

function FilePick(props: { accept: string; label: string; onFile: (file: File) => Promise<void> }): JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <label class="btn btn-full" style={{ cursor: 'pointer' }}>
      {busy ? 'Working…' : props.label}
      <input
        type="file"
        accept={props.accept}
        class="sr-only"
        onChange={async (e) => {
          const input = e.target as HTMLInputElement;
          const file = input.files?.[0];
          if (!file) return;
          setBusy(true);
          try {
            await props.onFile(file);
          } catch (err) {
            notify(err instanceof Error ? err.message : 'Could not read that file', true);
          } finally {
            setBusy(false);
            input.value = '';
          }
        }}
      />
    </label>
  );
}

function StorageInfo(): JSX.Element {
  const [info, setInfo] = useState<string>('');
  const load = async () => {
    const counts: string[] = [];
    for (const store of ['foodLogs', 'doses', 'sessions', 'runs', 'metrics'] as const) {
      counts.push(`${(await getAll(store)).length} ${store}`);
    }
    let quota = '';
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      if (est.usage) quota = ` · ${fmt(est.usage / 1_048_576, 1)} MB used`;
    }
    setInfo(counts.join(' · ') + quota);
  };
  if (!info) void load();
  return <p class="note">{info || 'Counting…'}</p>;
}
